import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons, FlexContainer, Spinner } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import StorageService from 'Services/storage';
import EventService from 'Services/events';
import { showModalAction } from 'Store/modules/app/actions';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { createURL } from 'Containers/Plans/utils';
import { routes } from 'Constants/routes';
import { PAYMENT_METHOD_PA_MODAL } from 'Constants/modals';
import { openUrlInNewNavigator } from 'Utils/links';
import { WHATSAPP_AA_HELP } from 'Constants/whatsappLinks';
import { TAX_YEAR } from 'Config';
import * as Styled from './styles';
import {
  useAutoRequestPreviousYear,
  useFetchGetPreviousYearResult,
  useFetchGetPreviousYearsPercentage,
} from './hooks';
import { PreviousYearsResultType, PREVIOUS_YEAR_STORAGE_KEY, PreviousYearSatStatus } from './types';
import FaqToReturn from './components/FaqToReturn';
import { componentsByResult } from './utils';
import ResultBoxPending from './components/ResultBoxPending';

const PreviousYears = (): JSX.Element => {
  useAutoRequestPreviousYear();
  const { t } = useTranslation(locizeNS.PY);
  const info = useSelector(routeStateSelector);
  const dispatch = useDispatch();
  const { data, isLoading, isFetching } = useFetchGetPreviousYearResult();
  const { mutate, isLoading: loading, data: price } = useFetchGetPreviousYearsPercentage();
  const handleClickPayNow = (amount?: number): void => {
    dispatch(
      showModalAction({
        options: {
          type: PAYMENT_METHOD_PA_MODAL,
          data: {
            successUrl: createURL(routes.PREVIOUS_YEARS_WORKING),
            cancelUrl: createURL(routes.PREVIOUS_YEARS),
            upsellingProps: {
              price: {
                amount,
              },
              pendingAmount: data?.amountPending,
            },
            isPrefilingUser: info?.isPrefilingUser,
          },
        },
      })
    );
  };

  const isPending =
    data &&
    data?.records?.find(
      item => item?.lastStatus === PreviousYearSatStatus.NOT_AVAILABLE && item?.taxYear !== TAX_YEAR
    ) &&
    !data?.records?.some(item => item.isRecoverable);

  useEffect(() => {
    if (info?.isPrefilingUser && data?.amountPending) {
      handleClickPayNow(Number(data?.amountPending) * 100);
    }
  }, [info, data]);

  useEffect(() => {
    if (data && data?.amountPending !== 0) {
      StorageService.setItem(PREVIOUS_YEAR_STORAGE_KEY, data?.amountPending.toString());
      mutate({ amount: data.amountPending * 100 });
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      EventService.previousYearsResultView({
        result: isPending ? 'draft' : data?.refundStatusType,
        yearsDraft: data?.records
          ?.filter(
            item =>
              item?.lastStatus === PreviousYearSatStatus.NOT_AVAILABLE &&
              item?.taxYear !== '2019' &&
              item?.taxYear !== TAX_YEAR
          )
          .map(item => ({
            year: item?.taxYear || '',
          })),
        lostAmount: data?.records
          ?.filter(item => item?.taxYear === '2019' && item?.amountTotal !== 0)
          .map(item => ({
            year: item?.taxYear || '',
            result: item?.amountTotal?.toString() || '',
          })),
        allRecoverableResults: data?.records
          ?.filter(item => item?.isRecoverable)
          .map(item => ({
            year: item?.taxYear || '',
            result: item?.amountTotal?.toString() || '',
          })),
      });
    }
  }, [data]);

  const showSpinner =
    isLoading ||
    isFetching ||
    loading ||
    !data ||
    (!price && data?.refundStatusType === PreviousYearsResultType.TO_RETURN);

  const BoxComponent = isPending
    ? ResultBoxPending
    : data && componentsByResult[data?.refundStatusType];

  return (
    <Styled.ThreeColLayout>
      <Styled.TitleContainer type="headline" size="S_L_S" color="dark">
        {t('PreviousYears.title')}
      </Styled.TitleContainer>
      <FlexContainer direction="column" gap="dimension_24">
        <Styled.Title type="headline" size="S_L_S" color="dark">
          {t('PreviousYears.title')}
        </Styled.Title>
        <Styled.Container align="center" justify="center" direction="column" gap="dimension_24">
          {showSpinner ? (
            <Spinner size="L" align="center" justify="center" />
          ) : (
            <>
              <BoxComponent
                handleClickPayNow={handleClickPayNow}
                data={data}
                percentageInfo={price?.data?.item}
              />
              <FaqToReturn data={data} />
              <Buttons.Tiny
                label={t('PreviousYears.help')}
                onClick={() => {
                  openUrlInNewNavigator(WHATSAPP_AA_HELP);
                }}
              />
            </>
          )}
        </Styled.Container>
      </FlexContainer>
    </Styled.ThreeColLayout>
  );
};

export default PreviousYears;
